import _ from 'lodash';
import 'moment-timezone';
import moment from 'moment';
import { widgets } from '@ui-schema/ds-material';
import { UIMetaProvider } from '@ui-schema/ui-schema/UIMeta';
import { storeUpdater } from '@ui-schema/ui-schema/storeUpdater';
import { isInvalid } from '@ui-schema/ui-schema/ValidityReporter';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { relTranslator } from '@ui-schema/ui-schema/Translate/relT';
import { GridContainer } from '@ui-schema/ds-material/GridContainer';
import { createOrderedMap } from '@ui-schema/ui-schema/Utils/createMap';
import { injectPluginStack } from '@ui-schema/ui-schema/applyPluginStack';
import { UIStoreProvider, createStore } from '@ui-schema/ui-schema/UIStore';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import { defaultDateTimeFormat } from 'src/careflow/helpers/constants';
import useNotiMessage from 'src/hooks/useNotiMessage';

const GridStack = injectPluginStack(GridContainer);

type Props = {
  dataSchema: any;
  data: any;
  edit: any;
  defaultStructure: any;
  handleComplementaryInfo: (store: any) => Promise<any>;
};

const ComplementaryInfoEdit = ({
  dataSchema,
  data,
  edit,
  defaultStructure,
  handleComplementaryInfo
}: Props) => {
  const { scope = {} } = data;
  const { checklistItem = {} } = scope;
  const tenantConfigData = useTenantConfigData();
  const { showErrorMessage } = useNotiMessage();

  const [showValidity, setShowValidity] = useState(false);
  const formattedData = _.merge(checklistItem, defaultStructure);

  const dateTimeFormat = useMemo(() => {
    return tenantConfigData?.configurations?.dateTimeFormat || defaultDateTimeFormat;
  }, [tenantConfigData?.configurations?.dateTimeFormat]);

  const modifiedSchema = useMemo(() => {
    if (!dataSchema) return dataSchema;

    const clonedSchema = _.cloneDeep(dataSchema);

    const updateDateHourField = (obj: any): boolean => {
      if (!obj || typeof obj !== 'object') return false;

      if (obj.title === 'Schedule HJ Date and Hour' && obj.type === 'string') {
        if (obj.$ref) delete obj.$ref;
        if (obj.widget?.$ref) delete obj.widget;
        obj.inputFormat = dateTimeFormat;
        obj.isTimeIncluded = true;
        obj.format = 'date-time';
        return true;
      }

      return Object.keys(obj).some((key) => updateDateHourField(obj[key]));
    };
    
    updateDateHourField(clonedSchema);
    return clonedSchema;
  }, [dataSchema, dateTimeFormat]);

  const schema: any = createOrderedMap(modifiedSchema);

  const updatedData = useMemo(() => {
    const dataCopy = _.cloneDeep(formattedData);

    const formatDateValues = (obj: any): void => {
      if (!obj || typeof obj !== 'object') return;

      const dateFields = ['validatedOn', 'start', 'end', 'nextContactOn', 'contactOn', 'date_hour'];
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const formatDateField = (value: string): string => {
        if (!value || typeof value !== 'string') return value;

        const isISOFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value);
        let momentDate;

        if (isISOFormat) {
          momentDate = moment(value);
          momentDate = momentDate.tz(timeZone);
        } else {
          momentDate = moment(value, dateTimeFormat, true);
          if (!momentDate.isValid()) {
            momentDate = moment(value);
          }
        }

        if (momentDate.isValid()) {
          return momentDate.format(dateTimeFormat);
        }

        return value;
      };

      if (obj.modifiedFields) {
        Object.keys(obj.modifiedFields).forEach((key) => {
          if (dateFields.includes(key) && typeof obj.modifiedFields[key] === 'string') {
            obj.modifiedFields[key] = formatDateField(obj.modifiedFields[key]);
          }
        });
      }

      dateFields.forEach((field) => {
        if (obj[field] && typeof obj[field] === 'string') {
          obj[field] = formatDateField(obj[field]);
        }
      });

      Object.keys(obj).forEach((key) => {
        if (typeof obj[key] === 'object' && obj[key] !== null) {
          formatDateValues(obj[key]);
        }
      });
    };

    formatDateValues(dataCopy);
    return dataCopy;
  }, [formattedData, dateTimeFormat]);

  const [store, setStore] = useState<any>(() => createStore(createOrderedMap(checklistItem)));

  useEffect(() => {
    setStore(createStore(createOrderedMap(updatedData)));
  }, [data, updatedData]);

  const detectAndShowDateError = useCallback((dateValue: string, format: string) => {
    if (!dateValue || typeof dateValue !== 'string' || dateValue.trim() === '') {
      return false;
    }

    const parsedDate = moment(dateValue, format, true);
    const datePart = dateValue.split(' ')[0];
    const parts = datePart.split('/');

    const checkInvalidDate = () => {
      if (parts.length >= 2) {
        const month = parseInt(parts[0], 10);
        const day = parseInt(parts[1], 10);

        if (!isNaN(month) && month > 12) {
          showErrorMessage('Invalid date: Month cannot be greater than 12. Please enter a valid date.');
          return true;
        }

        if (!isNaN(month) && !isNaN(day) && month >= 1 && month <= 12) {
          const daysInMonth = moment(`${month}/01/2024`, 'MM/DD/YYYY').daysInMonth();
          if (day > daysInMonth) {
            const monthName = moment(`${month}/01/2024`, 'MM/DD/YYYY').format('MMMM');
            showErrorMessage(`Invalid date: ${monthName} only has ${daysInMonth} days. Please enter a valid date.`);
            return true;
          }
        }
      }
      return false;
    };

    if (!parsedDate.isValid()) {
      if (!checkInvalidDate()) {
        showErrorMessage('Invalid date format. Please enter a valid date and time.');
      }
      return true;
    } else {
      const formatted = parsedDate.format(format);
      if (formatted !== dateValue) {
        if (!checkInvalidDate()) {
          showErrorMessage('Invalid date: The entered date does not exist. Please enter a valid date.');
        }
        return true;
      }
    }

    return false;
  }, [showErrorMessage]);

  const onChange = useCallback(
    (actions) => {
      if (Array.isArray(actions)) {
        actions.forEach((action: any) => {
          if (action?.type === 'update' && action?.schema?.get('title') === 'Schedule HJ Date and Hour') {
            const value = action?.value;
            if (value && typeof value === 'string' && value.trim() !== '') {
              if (detectAndShowDateError(value, dateTimeFormat)) {
                return;
              }
            }
          }
        });
      }
      setStore(storeUpdater(actions));
    },
    [setStore, dateTimeFormat, detectAndShowDateError]
  );

  useEffect(() => {
    if (edit) {
      if (isInvalid(store.getValidity())) {
        setShowValidity(true);
      } else {
        const values = store.valuesToJS();

        const validateDateHour = (obj: any): boolean => {
          if (!obj || typeof obj !== 'object') return true;

          const dateValue = obj.modifiedFields?.date_hour;
          if (dateValue && typeof dateValue === 'string' && dateValue.trim() !== '') {
            if (detectAndShowDateError(dateValue, dateTimeFormat)) {
              setShowValidity(true);
              return false;
            }
          }

          return Object.values(obj).every((value) =>
            typeof value !== 'object' || value === null || validateDateHour(value)
          );
        };

        if (validateDateHour(values)) {
          handleComplementaryInfo(values);
        }
      }
    }
  }, [edit, store, dateTimeFormat, handleComplementaryInfo, detectAndShowDateError])

  return (
    <>
      <UIMetaProvider widgets={widgets} t={relTranslator}>
        <UIStoreProvider store={store} onChange={onChange} showValidity={showValidity}>
          <GridStack isRoot schema={schema} />
        </UIStoreProvider>
      </UIMetaProvider>
    </>
  );
};

export default ComplementaryInfoEdit;
